<template>
  <div class="crawl-status flex-grow-1 d-flex flex-column overflow-y-hidden">
    <div class="a-header">
      <span class="a-header--title slide-down-enter-active">Crawl Status</span>
    </div>

    <div v-if="isLoading" class="d-flex justify-content-center align-items-center pt-35-px">
      <Spinner />
    </div>

    <transition name="slide-up">
      <div v-if="!isLoading" class="a-body p-20-px flex-grow-1 overflow-y-auto">
        <div class="status-section mb-20-px">
          <a-row :gutter="24">
            <a-col :span="16">
              <a-descriptions bordered :column="2" size="small">
                <a-descriptions-item label="Crawl ID">{{ crawlId }}</a-descriptions-item>
                <a-descriptions-item label="Status">
                  <a-tag :color="statusColor">{{ crawlData.status || '-' }}</a-tag>
                </a-descriptions-item>
                <a-descriptions-item label="Crawl Depth">{{ crawlData.max_depth || '-' }}</a-descriptions-item>
                <a-descriptions-item label="Page Limit">{{ crawlData.page_limit || '-' }}</a-descriptions-item>
                <a-descriptions-item label="Depth Mode">{{ getDepthModeLabel(crawlData.depth_mode) }}</a-descriptions-item>
                <a-descriptions-item label="URL Report">{{ getUrlReportModeLabel(crawlData.url_report_mode) }}</a-descriptions-item>
                <a-descriptions-item label="Created">
                  <template v-if="crawlData.created_at">
                    {{ crawlData.created_at | dateString }} {{ crawlData.created_at | timeString }}
                  </template>
                  <template v-else>-</template>
                </a-descriptions-item>
                <a-descriptions-item label="Completed">
                  <template v-if="crawlData.completed_at">
                    {{ crawlData.completed_at | dateString }} {{ crawlData.completed_at | timeString }}
                  </template>
                  <template v-else>-</template>
                </a-descriptions-item>
              </a-descriptions>
            </a-col>
          </a-row>
        </div>

        <div
          v-if="monitoringBatchResult && monitoringBatchResult.batches && monitoringBatchResult.batches.length > 0"
          class="mb-20-px monitoring-batch-success"
        >
          <div class="success-rebaseline-inner">
            <div>
              <a-icon type="check-circle" theme="filled" class="check-success" />
            </div>
            <div class="ml-3">
              <p class="mb-1 notice-text">{{ monitoringBatchResult.totalUrls }} webpages queued for baseline (batch jobs).</p>
              <p v-if="monitoringBatchResult.batches.length > 1" class="mb-1 notice-text">
                <strong>Split into {{ monitoringBatchResult.batches.length }} batch jobs</strong> (up to {{ maxBatchBaselineSize }} URLs per job).
              </p>
              <p class="mb-1 notice-text">This may take a few minutes to a few hours depending on volume.</p>
              <p class="mb-1 notice-text">
                Track {{ monitoringBatchResult.batches.length > 1 ? 'these jobs' : 'this job' }}:
              </p>
              <ul class="batch-list mb-1">
                <li v-for="batch in monitoringBatchResult.batches" :key="batch.batchId" class="notice-text">
                  <router-link class="nav-link-batch" :to="{ path: '/batch-jobs/detail/BL' + batch.batchId }">
                    Batch ID BL{{ batch.batchId }}
                  </router-link>
                  ({{ batch.total }} URLs<span v-if="batch.processed !== batch.total">, {{ batch.processed }} processed</span>)
                </li>
              </ul>
              <p v-if="parentUserName" class="mb-0 notice-text">An email will be sent to {{ parentUserName }} when each job finishes.</p>
            </div>
          </div>
          <div v-if="monitoringBatchResult.quotaExhausted" class="warning-inner mt-2">
            <a-icon type="warning" theme="filled" class="warning-icon" />
            <div class="ml-3">
              <p class="mb-0 notice-text">
                <strong>Quota limit reached.</strong> {{ monitoringBatchResult.remainingUrlsCount }} URL(s) were not queued.
              </p>
            </div>
          </div>
          <div v-if="monitoringBatchResult.failedBatch" class="error-inner mt-2">
            <a-icon type="close-circle" theme="filled" class="error-icon" />
            <div class="ml-3">
              <p class="mb-1 notice-text">
                <strong>Batch {{ monitoringBatchResult.failedBatch.chunkIndex }} failed</strong> ({{ monitoringBatchResult.failedBatch.urlCount }} URLs)
              </p>
              <p class="mb-0 notice-text">{{ (monitoringBatchResult.failedBatch.error || []).join(', ') }}</p>
            </div>
          </div>
          <a-button type="link" size="small" class="mt-2 px-0" @click="monitoringBatchResult = null">Dismiss</a-button>
        </div>

        <div v-if="jobs.length > 0" class="progress-section mb-20-px">
          <span class="progress-text">
            {{ completedJobCount }} of {{ jobs.length }} domains completed
          </span>
        </div>

        <div v-if="isPolling" class="polling-indicator mb-20-px">
          <a-spin size="small" />
          <span class="ml-2">Crawling in progress... checking every 5 seconds</span>
          <a-button
            type="danger"
            size="small"
            ghost
            class="ml-3"
            :loading="isCancelling"
            @click="showCancelConfirm"
          >
            Cancel Crawl
          </a-button>
        </div>

        <div v-if="isCancelled" class="mb-20-px">
          <a-alert message="Crawl Cancelled" description="This crawl was cancelled before it could complete." type="warning" show-icon />
        </div>

        <div v-if="isFailed" class="mb-20-px">
          <a-alert message="Crawl Failed" description="The crawl request has failed. Please try again." type="error" show-icon />
        </div>

        <div v-if="jobs.length > 0" class="jobs-section">
          <div class="d-flex align-items-center justify-content-between mb-3">
            <h3 class="results-title mb-0">Domains ({{ jobs.length }})</h3>
            <div v-if="hasAnyResults" class="d-flex align-items-center">
              <a-checkbox
                :checked="isAllSelected"
                :indeterminate="isIndeterminate"
                @change="onToggleSelectAll"
              >
                {{ isAllSelected ? 'Deselect All' : 'Select All' }}
              </a-checkbox>
              <a-button
                type="primary"
                class="ml-3"
                :disabled="selectedUrls.length === 0"
                @click="showMonitoringModal"
              >
                <a-icon type="plus-circle" />
                Add to Monitoring ({{ selectedUrls.length }})
              </a-button>
            </div>
          </div>

          <div v-if="isLoadingResults" class="d-flex align-items-center mb-20-px">
            <a-spin size="small" />
            <span class="ml-2">Loading crawl results from storage...</span>
          </div>

          <a-collapse v-model="activeJobPanels">
            <a-collapse-panel v-for="job in jobs" :key="job.id" :header="job.url">
              <template slot="extra">
                <a-tag :color="tagStatusColor(job.status)" @click.stop>{{ job.status }}</a-tag>
                <span class="result-count">
                  {{ getJobResultCount(job) }} URLs
                </span>
                <span v-if="getUnknownDepthCount(job) > 0" class="result-count">
                  {{ getUnknownDepthCount(job) }} unknown depth
                </span>
              </template>
              <div v-if="job.status === CrawlStatusEnum.Completed && getJobResults(job).length > 0">
                <div class="level-sections">
                  <a-collapse :bordered="true">
                    <a-collapse-panel
                      v-for="levelSection in getJobLevels(job)"
                      :key="levelSection.key"
                      :showArrow="true"
                      class="level-panel"
                    >
                      <template slot="header">
                        <div class="level-header">
                          <span class="level-header__label">
                            {{ levelSection.label }} ({{ levelSection.urls.length }} {{ levelSection.urls.length === 1 ? 'URL' : 'URLs' }})
                          </span>
                        </div>
                      </template>
                      <template slot="extra">
                        <a-checkbox
                          :checked="isLevelAllSelected(job, levelSection.key)"
                          :indeterminate="isLevelIndeterminate(job, levelSection.key)"
                          @click.stop
                          @change="(e) => onToggleLevelSelectAll(job, levelSection.key, e.target.checked)"
                        >
                          Select All
                        </a-checkbox>
                      </template>
                      <div class="level-urls">
                        <div v-for="(result, resultIndex) in levelSection.urls" :key="result.url" class="result-item d-flex align-items-center">
                          <a-checkbox
                            :checked="isUrlSelected(result.url)"
                            @change="(e) => onToggleUrl(result.url, e.target.checked)"
                          />
                          <span class="result-index ml-2">{{ resultIndex + 1 }}.</span>
                          <a v-if="isSafeUrl(result.url)" :href="result.url" target="_blank" rel="noopener noreferrer" class="ml-1" @click.stop>{{ result.url }}</a>
                          <span v-else class="ml-1">{{ result.url }}</span>
                        </div>
                      </div>
                    </a-collapse-panel>
                  </a-collapse>
                </div>
              </div>
              <div v-else-if="job.status === CrawlStatusEnum.Completed && !isLoadingResults && getJobResults(job).length === 0">
                <a-alert message="No URLs discovered for this domain." type="info" banner />
              </div>
              <div v-else-if="job.status === CrawlStatusEnum.Completed && isLoadingResults">
                <div class="d-flex align-items-center">
                  <a-spin size="small" />
                  <span class="ml-2">Loading results...</span>
                </div>
              </div>
              <div v-else-if="job.status === CrawlStatusEnum.Failed">
                <a-alert message="Crawl failed for this domain." type="error" banner />
              </div>
              <div v-else class="d-flex align-items-center">
                <a-spin size="small" />
                <span class="ml-2">Processing...</span>
              </div>
            </a-collapse-panel>
          </a-collapse>
        </div>

        <div v-if="isCompleted && jobs.length === 0">
          <a-alert message="No domains found" description="The crawl completed but no results are available." type="info" show-icon />
        </div>
      </div>
    </transition>

    <div class="a-footer">
      <a-button size="large" class="slide-down-enter-active">
        <router-link to="/web-crawl/history">Back to History</router-link>
      </a-button>
    </div>

    <a-modal
      title="Add URLs to Monitoring"
      :visible="monitoringModalVisible"
      :width="720"
      :confirm-loading="isAddingToMonitoring"
      ok-text="Add to Monitoring"
      :ok-button-props="{ disabled: !canSubmitMonitoring }"
      @ok="onAddToMonitoring"
      @cancel="closeMonitoringModal"
    >
      <p class="mb-3">You have selected <strong>{{ selectedUrls.length }}</strong> URL(s).</p>
      <a-form layout="vertical">
        <a-form-item label="Plan type" required>
          <a-select v-model="monitoringPlanType" placeholder="Select plan" size="large" style="width: 100%">
            <template v-for="(valuePlanType, keyPlanType) in planTypes">
              <a-select-option
                v-if="valuePlanType.limit - valuePlanType.count > 0"
                :key="keyPlanType"
                :value="keyPlanType"
              >
                {{ keyPlanType | lowercase | capitalize }} — {{ valuePlanType.limit - valuePlanType.count }} URLs left
              </a-select-option>
            </template>
          </a-select>
        </a-form-item>
        <a-form-item label="User Agent" required>
          <a-select v-model="monitoringUserAgentId" size="large" style="width: 100%">
            <a-select-option v-for="ua in userAgents" :key="ua.id" :value="ua.id">{{ ua.alias }}</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item>
          <a-checkbox :checked="monitoringAdvancedOpen" @change="(e) => (monitoringAdvancedOpen = e.target.checked)">
            Advanced options
          </a-checkbox>
        </a-form-item>
        <template v-if="monitoringAdvancedOpen">
          <a-row :gutter="16">
            <a-col :span="12">
              <a-form-item label="Keyword(s) to include" extra="Separate with commas">
                <a-textarea v-model="monitoringKeywordsInclude" :rows="4" />
              </a-form-item>
            </a-col>
            <a-col :span="12">
              <a-form-item label="Keyword(s) to exclude" extra="Separate with commas">
                <a-textarea v-model="monitoringKeywordsExclude" :rows="4" />
              </a-form-item>
            </a-col>
          </a-row>
          <a-form-item v-if="featureAccess && featureAccess['tags']" label="Tags">
            <a-select
              v-model="monitoringTagIds"
              mode="multiple"
              placeholder="Select tags"
              size="large"
              style="width: 100%"
              show-search
              option-filter-prop="children"
            >
              <a-select-option v-for="tag in availableTags" :key="tag.id" :value="tag.id">{{ tag.name }}</a-select-option>
            </a-select>
          </a-form-item>
        </template>
      </a-form>
    </a-modal>
  </div>
</template>

<script>
  import WebCrawlService from '../services/web-crawl.service';
  import {
    CrawlStatusEnum,
    CrawlStatusColorTag,
    getDepthModeLabel,
    getUrlReportModeLabel,
  } from '../consts/CrawlStatus.consts';
  import { formatApiError } from '../helpers/format-api-error';
  import { normalizeCrawlJobResults } from '../helpers/normalize-crawl-results';
  import DataService from '@shared/services/data.service';
  import AccountService from '../../Account/services/account.service';
  import TagsService from '../../Tags/services/tags.service';
  import {
    runBatchBaselineForUrls,
    fetchPlanUsage,
    MAX_BATCH_BASELINE_SIZE,
  } from '../../Webpages/helpers/batch-baseline-multiples';
  import { DEFAULT_USER_AGENT_ID } from '../../Webpages/consts/Webpage.consts';

  const POLL_INTERVAL = 5000;
  const POLL_BACKOFF_INTERVAL = 10000;
  const MAX_POLL_COUNT = 720;

  export default {
    name: 'CrawlStatus',
    data() {
      return {
        CrawlStatusEnum,
        maxBatchBaselineSize: MAX_BATCH_BASELINE_SIZE,
        crawlId: this.$route.params.id,
        crawlData: {},
        resultsData: {},
        resultsByLevel: {},
        unknownDepthCountByJob: {},
        isLoading: true,
        isLoadingResults: false,
        hasFetchedStatus: false,
        pollTimer: null,
        pollCount: 0,
        isDestroyed: false,
        selectedUrlSet: {},
        activeJobPanels: [],
        monitoringModalVisible: false,
        monitoringMasterDataLoaded: false,
        monitoringPlanType: 'TRIAL',
        monitoringUserAgentId: DEFAULT_USER_AGENT_ID,
        monitoringKeywordsInclude: '',
        monitoringKeywordsExclude: '',
        monitoringTagIds: [],
        monitoringAdvancedOpen: false,
        userAgents: [],
        planTypes: {},
        availableTags: [],
        parentUserName: '',
        monitoringBatchResult: null,
        isAddingToMonitoring: false,
        isCancelling: false,
      };
    },
    computed: {
      featureAccess() {
        return (this.$store.state.user && this.$store.state.user.data && this.$store.state.user.data.feature_access) || {};
      },
      canSubmitMonitoring() {
        if (this.selectedUrls.length === 0) return false;
        if (!this.monitoringPlanType || !this.monitoringUserAgentId) return false;
        if (!this.monitoringMasterDataLoaded) return false;
        const pu = this.planTypes[this.monitoringPlanType];
        return pu && pu.limit > pu.count;
      },
      currentStatus() {
        return this.crawlData.status || '';
      },
      statusColor() {
        return CrawlStatusColorTag[this.currentStatus] || 'default';
      },
      isPolling() {
        return this.currentStatus === CrawlStatusEnum.Pending || this.currentStatus === CrawlStatusEnum.Processing;
      },
      isCompleted() {
        return this.currentStatus === CrawlStatusEnum.Completed;
      },
      isFailed() {
        return this.currentStatus === CrawlStatusEnum.Failed;
      },
      isCancelled() {
        return this.currentStatus === CrawlStatusEnum.Cancelled;
      },
      jobs() {
        return this.crawlData.jobs || [];
      },
      completedJobCount() {
        return this.jobs.filter((j) => j.status === CrawlStatusEnum.Completed).length;
      },
      allDiscoveredUrls() {
        const urls = [];
        this.jobs.forEach((job) => {
          const results = (this.resultsData[job.id] || []);
          results.forEach((r) => {
            if (r.url) urls.push(r.url);
          });
        });
        return urls;
      },
      hasAnyResults() {
        return this.allDiscoveredUrls.length > 0;
      },
      selectedUrls() {
        return Object.keys(this.selectedUrlSet).filter((url) => this.selectedUrlSet[url]);
      },
      isAllSelected() {
        return this.allDiscoveredUrls.length > 0 && this.selectedUrls.length === this.allDiscoveredUrls.length;
      },
      isIndeterminate() {
        return this.selectedUrls.length > 0 && this.selectedUrls.length < this.allDiscoveredUrls.length;
      },
      tagStatusColor() {
        return (status) => CrawlStatusColorTag[status] || 'default';
      },
      isSafeUrl() {
        return (url) => {
          try {
            const parsed = new URL(url);
            return parsed.protocol === 'http:' || parsed.protocol === 'https:';
          } catch {
            return false;
          }
        };
      },
    },
    mounted() {
      this.fetchStatus();
    },
    beforeDestroy() {
      this.isDestroyed = true;
      this.clearPollTimer();
    },
    methods: {
      getDepthModeLabel,
      getUrlReportModeLabel,
      clearPollTimer() {
        if (this.pollTimer) {
          clearTimeout(this.pollTimer);
          this.pollTimer = null;
        }
      },
      getJobResults(job) {
        return this.resultsData[job.id] || [];
      },
      getJobResultCount(job) {
        const loaded = this.getJobResults(job);
        if (loaded.length > 0) return loaded.length;
        return job.result_url_count || 0;
      },
      getUnknownDepthCount(job) {
        const loadedCount = this.unknownDepthCountByJob[job.id];
        if (loadedCount != null) return loadedCount;
        return 0;
      },
      isUrlSelected(url) {
        return !!this.selectedUrlSet[url];
      },
      onToggleUrl(url, checked) {
        if (checked) {
          this.$set(this.selectedUrlSet, url, true);
        } else {
          this.$delete(this.selectedUrlSet, url);
        }
      },
      onToggleSelectAll(e) {
        const checked = e.target.checked;
        const newSet = {};
        if (checked) {
          this.allDiscoveredUrls.forEach((url) => {
            newSet[url] = true;
          });
        }
        this.selectedUrlSet = newSet;
      },
      getJobLevels(job) {
        return this.resultsByLevel[job.id] || [];
      },
      getJobLevelUrls(job, levelKey) {
        const levels = this.getJobLevels(job);
        const section = levels.find((s) => s.key === levelKey);
        return section ? section.urls.map((u) => u.url) : [];
      },
      isLevelAllSelected(job, levelKey) {
        const urls = this.getJobLevelUrls(job, levelKey);
        return urls.length > 0 && urls.every((url) => !!this.selectedUrlSet[url]);
      },
      isLevelIndeterminate(job, levelKey) {
        const urls = this.getJobLevelUrls(job, levelKey);
        const selectedCount = urls.filter((url) => !!this.selectedUrlSet[url]).length;
        return selectedCount > 0 && selectedCount < urls.length;
      },
      onToggleLevelSelectAll(job, levelKey, checked) {
        const urls = this.getJobLevelUrls(job, levelKey);
        const newSet = { ...this.selectedUrlSet };
        urls.forEach((url) => {
          if (checked) {
            newSet[url] = true;
          } else {
            delete newSet[url];
          }
        });
        this.selectedUrlSet = newSet;
      },
      async showMonitoringModal() {
        if (!this.monitoringMasterDataLoaded) {
          await this.loadMonitoringMasterData();
          this.monitoringMasterDataLoaded = true;
        }
        this.monitoringModalVisible = true;
      },
      closeMonitoringModal() {
        this.monitoringModalVisible = false;
      },
      async loadMonitoringMasterData() {
        try {
          const userId = (this.$store.state.user && this.$store.state.user.data && this.$store.state.user.data.id) || null;
          const [userAgentsData, userData, tagsData, account] = await Promise.all([
            DataService.getUserAgents(),
            DataService.getUsers(),
            userId ? TagsService.getList(userId) : Promise.resolve([]),
            AccountService.getUser().catch(() => ({})),
          ]);
          this.userAgents = userAgentsData || [];
          this.planTypes = userData.plan_usage || {};
          this.availableTags = tagsData || [];
          this.parentUserName = (account && account.name) || '';

          if (this.userAgents.length) {
            const hasUa = this.userAgents.some((u) => u.id === this.monitoringUserAgentId);
            if (!hasUa) this.monitoringUserAgentId = this.userAgents[0].id;
          }

          const firstWithQuota = Object.keys(this.planTypes).find(
            (k) => this.planTypes[k].limit > this.planTypes[k].count
          );
          if (firstWithQuota) {
            this.monitoringPlanType = firstWithQuota;
          }
        } catch (error) {
          this.$showErrorMessage(formatApiError(error, 'Failed to load form options.'));
        }
      },
      async onAddToMonitoring() {
        if (this.isAddingToMonitoring) return;
        if (!this.canSubmitMonitoring) {
          this.$showErrorMessage('Please select a valid plan with quota and user agent.');
          return;
        }
        const urls = this.selectedUrls;
        const baseBody = {
          plan_type: this.monitoringPlanType,
          user_agent_id: this.monitoringUserAgentId,
          text_string_appear: this.monitoringKeywordsInclude || '',
          text_string_not_appear: this.monitoringKeywordsExclude || '',
          tag_ids: Array.isArray(this.monitoringTagIds) ? this.monitoringTagIds : [],
        };

        this.isAddingToMonitoring = true;
        try {
          this.planTypes = await fetchPlanUsage();
          const result = await runBatchBaselineForUrls(urls, baseBody, this.planTypes);
          this.planTypes = await fetchPlanUsage();

          if (result.initialQuotaRejected) {
            this.$showErrorMessage('No quota remaining for the selected plan type.');
            return;
          }

          const {
            successfulBatches,
            totalUrls,
            failedBatch,
            quotaExhausted,
            remainingUrls,
          } = result;

          if (successfulBatches.length > 0) {
            const batchCount = successfulBatches.length;
            if (!failedBatch && !quotaExhausted) {
              this.$showSuccessMessage(
                batchCount > 1
                  ? `${batchCount} batch jobs created for ${totalUrls} webpage(s)`
                  : 'Batch job created for selected webpage(s)'
              );
            } else {
              this.$showSuccessMessage(`${successfulBatches.length} batch job(s) created for ${totalUrls} webpage(s).`);
            }
            this.monitoringBatchResult = {
              batches: successfulBatches,
              totalUrls,
              failedBatch,
              quotaExhausted,
              remainingUrlsCount: remainingUrls.length,
            };
            this.monitoringModalVisible = false;
            this.selectedUrlSet = {};
          } else if (failedBatch) {
            this.$showMultiErrorMessage(failedBatch.error);
          }
        } catch (error) {
          this.$showErrorMessage(formatApiError(error, 'Failed to queue URLs for baseline.'));
        } finally {
          this.isAddingToMonitoring = false;
        }
      },
      showCancelConfirm() {
        this.$confirm({
          title: 'Cancel this crawl?',
          content: 'The crawl will be stopped and cannot be resumed.',
          okText: 'Cancel Crawl',
          okType: 'danger',
          cancelText: 'Keep Running',
          onOk: () => this.onCancelCrawl(),
        });
      },
      async onCancelCrawl() {
        if (this.isCancelling) return;
        this.isCancelling = true;
        try {
          await WebCrawlService.cancelCrawl(this.crawlId);
          this.$showSuccessMessage('Crawl cancelled.');
          this.clearPollTimer();
          await this.fetchStatus();
        } catch (error) {
          const status = error && error.response && error.response.status;
          if (status === 409) {
            this.$showErrorMessage('This crawl can no longer be cancelled.');
          } else {
            this.$showErrorMessage(formatApiError(error, 'Failed to cancel crawl.'));
          }
          await this.fetchStatus();
        } finally {
          this.isCancelling = false;
        }
      },
      async fetchResults() {
        if (this.isLoadingResults) return;
        this.isLoadingResults = true;
        try {
          const data = await WebCrawlService.getCrawlResults(this.crawlId);
          if (this.isDestroyed) return;
          const resultsByJob = {};
          const levelsByJob = {};
          const unknownDepthCounts = {};
          const jobsList = (data && Array.isArray(data.data) && data.data) || [];
          jobsList.forEach((job) => {
            const jobId = job.job_id;
            const { results, levelSections } = normalizeCrawlJobResults(job);
            resultsByJob[jobId] = results;
            levelsByJob[jobId] = levelSections;
            const unknownSection = levelSections.find((section) => section.key === 'unknown');
            unknownDepthCounts[jobId] =
              (unknownSection && unknownSection.urls.length) || 0;
          });
          this.resultsData = resultsByJob;
          this.resultsByLevel = levelsByJob;
          this.unknownDepthCountByJob = unknownDepthCounts;
        } catch (error) {
          this.$showErrorMessage(formatApiError(error, 'Failed to fetch crawl results.'));
        } finally {
          this.isLoadingResults = false;
        }
      },
      schedulePoll(interval) {
        if (this.isDestroyed) return;
        if (this.pollCount >= MAX_POLL_COUNT) {
          this.$showErrorMessage('Crawl is taking longer than expected. Please refresh to check status.');
          return;
        }
        this.pollCount++;
        this.pollTimer = setTimeout(() => this.fetchStatus(), interval);
      },
      async fetchStatus() {
        try {
          const data = await WebCrawlService.getCrawl(this.crawlId);
          if (this.isDestroyed) return;
          this.crawlData = data || {};
          this.hasFetchedStatus = true;

          const hasCompletedJobs = this.completedJobCount > 0;

          if (this.isPolling) {
            if (hasCompletedJobs) this.fetchResults();
            this.schedulePoll(POLL_INTERVAL);
          } else if (this.isCompleted) {
            this.fetchResults();
          }
        } catch (error) {
          this.$showErrorMessage(formatApiError(error, 'Failed to fetch crawl status.'));
          if (!this.isDestroyed && (this.isPolling || !this.hasFetchedStatus)) {
            this.schedulePoll(POLL_BACKOFF_INTERVAL);
          }
        } finally {
          if (this.isLoading) {
            setTimeout(() => {
              this.isLoading = false;
            }, 300);
          }
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .polling-indicator {
    display: flex;
    align-items: center;
    color: $prLgt;
    font-size: 14px;
  }

  .progress-text {
    font-size: 15px;
    font-weight: 500;
  }

  .results-title {
    font-size: 16px;
    font-weight: 600;
  }

  .result-count {
    font-size: 12px;
    color: $prLgt;
    margin-left: 8px;
  }

  .result-item {
    padding: 6px 0;
    font-size: 13px;

    a {
      color: $ace;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  .result-index {
    display: inline-block;
    min-width: 30px;
    color: $prLgt;
  }

  .level-sections {
    padding: 0;

    ::v-deep .ant-collapse > .ant-collapse-item > .ant-collapse-header {
      padding: 12px 16px 12px 40px;
      padding-right: 120px;
      background-color: #fafafa;
      position: relative;
    }

    ::v-deep .ant-collapse > .ant-collapse-item .ant-collapse-extra {
      position: absolute;
      right: 16px;
      top: 50%;
      transform: translateY(-50%);
    }

    ::v-deep .ant-collapse-content > .ant-collapse-content-box {
      padding: 0;
    }
  }

  .level-panel {
    background-color: transparent;
  }

  .level-header {
    display: flex;
    align-items: center;

    &__label {
      font-size: 14px;
      font-weight: 500;
      color: #323232;
    }
  }

  .level-urls {
    padding: 8px 16px;
  }

  .monitoring-batch-success {
    .success-rebaseline-inner {
      padding: 8px 10px;
      border: 1px solid #7ecf42;
      border-radius: 4px;
      background-color: #f3ffea;
      display: flex;
      align-items: flex-start;
    }

    .check-success {
      font-size: 40px;
      color: rgb(126, 207, 66);
    }

    .notice-text {
      font-size: 1rem;
    }

    .batch-list {
      list-style: none;
      padding-left: 0;
      margin-bottom: 0.5rem;

      li {
        margin-bottom: 0.25rem;

        &::before {
          content: '•';
          margin-right: 0.5rem;
          color: rgb(126, 207, 66);
        }
      }
    }

    .nav-link-batch {
      color: $ace;

      &:hover {
        text-decoration: underline;
      }
    }

    .warning-inner {
      padding: 8px 10px;
      border: 1px solid #faad14;
      border-radius: 4px;
      background-color: #fffbe6;
      display: flex;
      align-items: flex-start;
    }

    .warning-icon {
      font-size: 40px;
      color: #faad14;
    }

    .error-inner {
      padding: 8px 10px;
      border: 1px solid #ff4d4f;
      border-radius: 4px;
      background-color: #fff2f0;
      display: flex;
      align-items: flex-start;
    }

    .error-icon {
      font-size: 40px;
      color: #ff4d4f;
    }
  }
</style>
